import React, { useState } from 'react';
import moment from 'moment';
import { Modal, Button, Icon } from './ui';
import './DateFilter.css';

/*
 * DateFilter — a date/range filter that COMMITS ON CONFIRM, not on each keystroke,
 * so picking a range doesn't fire a query the moment you set the first date. A small
 * trigger button opens a modal: choose a Single day (that whole 24h) or a Range
 * (from–to), then Apply. Leaving it off keeps the live, rolling feed. Shared by the
 * Events feed and the User Log.
 *   value   : {sDate,eDate} (YYYY-MM-DD) — the committed filter ({} = none)
 *   onApply : ({sDate,eDate}) => void
 *   onClear : () => void
 */
const fmt = (d, f) => moment(d, 'YYYY-MM-DD').format(f);

function labelFor(v) {
  // "All dates" to match the sibling filters ("All customers / sites / gates / guards")
  if (!v || (!v.sDate && !v.eDate)) return 'All dates';
  if (v.sDate && v.eDate) {
    if (v.sDate === v.eDate) return fmt(v.sDate, 'MMM D, YYYY');
    return `${fmt(v.sDate, 'MMM D')} – ${fmt(v.eDate, 'MMM D, YYYY')}`;
  }
  return fmt(v.sDate || v.eDate, 'MMM D, YYYY');
}

export default function DateFilter({ value = {}, onApply, onClear, disabled, label = 'date' }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('single');
  const [single, setSingle] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const openModal = () => {
    const v = value || {};
    if (v.sDate && v.eDate && v.sDate !== v.eDate) { setMode('range'); setFrom(v.sDate); setTo(v.eDate); setSingle(''); }
    else if (v.sDate || v.eDate) { setMode('single'); setSingle(v.sDate || v.eDate); setFrom(''); setTo(''); }
    else { setMode('single'); setSingle(''); setFrom(''); setTo(''); }
    setOpen(true);
  };

  const canApply = mode === 'single' ? !!single : !!(from && to);
  const apply = () => {
    if (mode === 'single' && single) {
      onApply({ sDate: single, eDate: single });
    } else if (mode === 'range' && from && to) {
      const [a, b] = from <= to ? [from, to] : [to, from];   // tolerate a reversed pick
      onApply({ sDate: a, eDate: b });
    } else {
      setOpen(false); return;
    }
    setOpen(false);
  };
  const clear = () => { onClear(); setOpen(false); };
  const hasValue = !!(value && (value.sDate || value.eDate));

  return (
    <>
      <button
        type="button"
        className={`datefilter${hasValue ? ' is-active' : ''}`}
        disabled={disabled}
        onClick={openModal}
        title={`Filter by ${label}`}
      >
        <Icon name="calendar" size={15} />
        <span className="datefilter__label">{labelFor(value)}</span>
        <Icon name="chevronDown" size={12} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Filter by ${label}`}
        size="sm"
        footer={
          <div className="ui-modal__actions datefilter__foot">
            <Button variant="ghost" onClick={clear} disabled={!hasValue}>Clear</Button>
            <div className="row" style={{ gap: 8 }}>
              <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={apply} disabled={!canApply}>Apply</Button>
            </div>
          </div>
        }
      >
        <div className="datefilter__modal">
          <div className="datefilter__seg" role="tablist" aria-label="Date filter mode">
            <button type="button" role="tab" aria-selected={mode === 'single'} className={mode === 'single' ? 'on' : ''} onClick={() => setMode('single')}>Single day</button>
            <button type="button" role="tab" aria-selected={mode === 'range'} className={mode === 'range' ? 'on' : ''} onClick={() => setMode('range')}>Date range</button>
          </div>

          {mode === 'single' ? (
            <label className="datefilter__row">
              <span>Date</span>
              <input type="date" className="input" value={single} onChange={(e) => setSingle(e.target.value)} autoFocus />
            </label>
          ) : (
            <>
              <label className="datefilter__row">
                <span>From</span>
                <input type="date" className="input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} autoFocus />
              </label>
              <label className="datefilter__row">
                <span>To</span>
                <input type="date" className="input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
              </label>
            </>
          )}

          <p className="datefilter__hint">
            {mode === 'single'
              ? 'Shows that whole day. Leave the filter off for the live, rolling feed.'
              : 'Pick both ends, then Apply — the filter runs only when you confirm.'}
          </p>
        </div>
      </Modal>
    </>
  );
}
